import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';

const inquirySchema = z.object({
  fullName: z.string().trim().min(1, 'Full name is required').max(100),
  email: z.string().trim().email('Invalid email address').max(255),
  phone: z.string().trim().min(7, 'Valid phone number required').max(20),
  organizationName: z.string().trim().min(1, 'Organization name is required').max(200),
  city: z.string().trim().min(1, 'City is required').max(100),
  state: z.string().trim().min(1, 'State is required').max(100),
  partnershipType: z.string().min(1, 'Select a partnership type'),
  eventType: z.string().min(1, 'Select an event type'),
  experienceLevel: z.string().min(1, 'Select your experience level'),
  message: z.string().trim().min(20, 'Message must be at least 20 characters').max(1000),
});


const API_BASE_URL = '';

const partnershipTypes = [
  'Event Organizer',
  'Venue Partner',
  'Promoter',
  'Sponsor',
  'Technology Partner',
];

const eventTypes = [
  'Concert / Live Music',
  'Festival',
  'Conference',
  'Sports Event',
  'Theater / Performing Arts',
  'Corporate Event',
  'Other',
];

const experienceLevels = [
  'New to event organizing' ,
  '1-3 years experience' ,
  '3-7 years experience' ,
  '7+ years experience' ,
];



const faqData = [
  {
    id: 1,
    question: 'How does facial recognition ticketing work?',
    answer: 'Our facial recognition technology allows you to enter events without physical tickets. Simply purchase tickets through our app, register your face, and walk into the venue. Our secure system verifies your identity in seconds.'
  },
  {
    id: 2,
    question: 'Is my facial data secure?',
    answer: 'Absolutely. We use bank-level encryption and never share your biometric data with third parties. Your facial data is only used for entry verification and is deleted after the event.'
  },
  {
    id: 3,
    question: 'How can I list my event on Woopy?',
    answer: 'Event organizers can partner with us by filling out our contact form or emailing us at partnerships@woopy.com. Our team will guide you through the onboarding process and help you leverage our technology.'
  },
  {
    id: 4,
    question: 'What if facial recognition doesn\'t work?',
    answer: 'We have backup verification methods including QR codes in the app and manual verification with ID. Our system has a 99.9% success rate, but we\'re always prepared with alternatives.'
  },
  {
    id: 5,
    question: 'Can I get a refund if I can\'t attend?',
    answer: 'Refund policies vary by event. Most events offer refunds up to 48 hours before the event date. Check the specific event page for detailed refund information.'
  },
  {
    id: 6,
    question: 'Do you charge service fees?',
    answer: 'We charge a small service fee to cover technology costs and platform maintenance. This fee is clearly displayed during checkout before you complete your purchase.'
  }
];

function Contact({ showNotification }) {
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

const {
  register,
  handleSubmit,
  reset,
  formState: { errors },
} = useForm({
  resolver: zodResolver(inquirySchema),
});

  const onSubmit = async (data) => {
    setSubmitStatus('loading');
    setErrorMessage('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const result = await res.json().catch(() => ({}));
        throw new Error(result.message || `Request failed with status ${res.status}`);
      }

      await res.json();
      setSubmitStatus('success');
      showNotification('Inquiry submitted successfully!', 'success');
      reset();
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    } catch (err) {
      setSubmitStatus('error');
      const message = err.message || 'Failed to submit inquiry. Please try again.';
      setErrorMessage(message);
      showNotification(message, 'error');
    }
  };

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  return (
    <main>
      {/* Page Header */}
      <section className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Get In Touch</h1>
          <p className="page-subtitle">We'd love to hear from you. Let's start a conversation.</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-container">
              <h2 className="form-title">Send us a message</h2>
              <p className="form-description">Fill out the form below and we'll get back to you within 24 hours.</p>

              {submitStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="rounded-xl border-2 border-green-200 bg-green-50 p-8 text-center"
                >
                  <CheckCircle className="mx-auto mb-4 text-green-600" size={48} />
                  <h3 className="text-xl font-bold text-green-900 mb-2">
                    Inquiry Submitted!
                  </h3>
                  <p className="text-green-700 mb-4">
                    We'll review your inquiry and contact you soon. Thank you for your interest in partnering with Woopy.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  onSubmit={handleSubmit(onSubmit)}
                  className="contact-form"
                >
                  {submitStatus === 'error' && (
                    <div className="mb-6 p-4 rounded-lg border-2 border-red-200 bg-red-50 flex items-start gap-3">
                      <AlertCircle className="text-red-600 mt-0.5" size={20} />
                      <p className="text-sm text-red-700">{errorMessage}</p>
                    </div>
                  )}

                  {/* Name & Email */}
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="fullName">Full Name *</label>
                      <input
                        type="text"
                        id="fullName"
                        placeholder="John Doe"
                        {...register('fullName')}
                      />
                      {errors.fullName && <p className="text-sm text-red-600 mt-1">{errors.fullName.message}</p>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        placeholder="you@example.com"
                        {...register('email')}
                      />
                      {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
                    </div>
                  </div>

                  {/* Phone & Organization */}
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        placeholder="+1 (212) 555-1234"
                        {...register('phone')}
                      />
                      {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone.message}</p>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="organizationName">Organization Name *</label>
                      <input
                        type="text"
                        id="organizationName"
                        placeholder="Your company or organization"
                        {...register('organizationName')}
                      />
                      {errors.organizationName && <p className="text-sm text-red-600 mt-1">{errors.organizationName.message}</p>}
                    </div>
                  </div>

                  {/* City & State */}
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input
                        type="text"
                        id="city"
                        placeholder="New York"
                        {...register('city')}
                      />
                      {errors.city && <p className="text-sm text-red-600 mt-1">{errors.city.message}</p>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="state">State *</label>
                      <input
                        type="text"
                        id="state"
                        placeholder="New York"
                        {...register('state')}
                      />
                      {errors.state && <p className="text-sm text-red-600 mt-1">{errors.state.message}</p>}
                    </div>
                  </div>

                  {/* Partnership Type */}
                  <div className="form-group">
                    <label htmlFor="partnershipType">Partnership Type *</label>
                    <select
                      id="partnershipType"
                      {...register('partnershipType')}
                    >
                      <option value="">Select a partnership type</option>
                      {partnershipTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {errors.partnershipType && <p className="text-sm text-red-600 mt-1">{errors.partnershipType.message}</p>}
                  </div>

                  {/* Event Type */}
                  <div className="form-group">
                    <label htmlFor="eventType">Event Type *</label>
                    <select
                      id="eventType"
                      {...register('eventType')}
                    >
                      <option value="">Select an event type</option>
                      {eventTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {errors.eventType && <p className="text-sm text-red-600 mt-1">{errors.eventType.message}</p>}
                  </div>

                  {/* Experience Level */}
                       <div className="form-group">
                    <label htmlFor="experienceLevel">Experience Level *</label>
                    <select
                      id="experienceLevel"
                      {...register('experienceLevel')}
                    >
                      <option value="">Select an experience level</option>
                      {experienceLevels.map((level) => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                    {errors.experienceLevel && <p className="text-sm text-red-600 mt-1">{errors.experienceLevel.message}</p>}
                  </div>

                

                  {/* Message */}
                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      rows="6"
                      placeholder="Tell us about your event, expected attendees, and how you'd like to partner with Woopy..."
                      {...register('message')}
                    ></textarea>
                    {errors.message && <p className="text-sm text-red-600 mt-1">{errors.message.message}</p>}
                  </div>

                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={submitStatus === 'loading'}
                  >
                    <span>{submitStatus === 'loading' ? 'Submitting...' : 'Submit Inquiry'}</span>
                    <Send size={18} />
                  </button>
                </motion.form>
              )}
            </div>









            {/* Contact Info */}
                  <div className="contact-info">
                    <div className="info-card">
                    <div className="info-icon">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <div className="info-content">
                      <h3>Our Office</h3>
                      <p>
                      1-A-23 Mahaveer Nagar 3rd,
                      <br />
                      Kota, Rajasthan 324005
                      <br />
                      Bharat
                      </p>
                    </div>
                    </div>

                    <div className="info-card">
                    <div className="info-icon">
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div className="info-content">
                      <h3>Email Us</h3>
                      <p>
                      <a href="mailto:support.whooppe@thrillathon.co.in">support.whooppe@thrillathon.co.in</a>
                      <br />
                      </p>
                    </div>
                    </div>

                    <div className="info-card">
                    <div className="info-icon">
                      <i className="fas fa-phone"></i>
                    </div>
                    <div className="info-content">
                      <h3>Call Us</h3>
                      <p>
                      <a href="tel:+918824223395">+91 88242-23395</a>
                      <br />
                      Mon-Sat, 9am-9pm IST
                      </p>
                    </div>
                    </div>

                    <div className="info-card">
                    <a href="https://wa.me/918824223395" target="_blank" rel="noopener noreferrer" className="info-icon">
                      <i className="fab fa-whatsapp"></i>
                    </a>
                    <div className="info-content">
                      <h3>WhatsApp Us</h3>
                      <p>
                      <a href="https://wa.me/918824223395" target="_blank" rel="noopener noreferrer">+91 88242-23395</a>
                      <br />
                     Available 24/7 Support
                      </p>
                    </div>
                    </div>

                    

                  </div>
                  </div>
                </div>
                </section>

                {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-description">Quick answers to common questions</p>

          <div className="faq-grid">
            {faqData.map((item) => (
              <div key={item.id} className="faq-item">
                <button className="faq-question" onClick={() => toggleFaq(item.id)}>
                  <span>{item.question}</span>
                  <i className={`fas ${expandedFaq === item.id ? 'fa-minus' : 'fa-plus'}`}></i>
                </button>
                <AnimatePresence>
                  {expandedFaq === item.id && (
                    <motion.div 
                      key="answer"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="faq-answer"
                    >
                      <p>{item.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Experience Events Differently?</h2>
            <p className="cta-description">Download the Whooppe app and discover a new way to attend live events</p>
            <a href="https://play.google.com/store/apps/details?id=com.thrillathon.whooppeapp" target="_blank" rel="noopener noreferrer" className="btn-cta">
              <i className="fab fa-google-play"></i>
              <div className="btn-text">
                <span className="btn-small">GET IT ON</span>
                <span className="btn-large">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <h3 className="footer-title">WHOOPPE</h3>
              <p className="footer-description">Biometric event ticketing technology. Enter any event with just your face.</p>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>
                  <a href="#/">About Woopy</a>
                </li>
                <li>
                  <a href="#/">Careers</a>
                </li>
                <li>
                  <Link to="/contact">Contact</Link>
                </li>
                <li>
                  <a href="#/">Press</a>
                </li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">For Organizers</h4>
              <ul className="footer-links">
                <li>
                  <a href="#/">List Your Show</a>
                </li>
                <li>
                  <a href="#/">Partner with Us</a>
                </li>
                <li>
                  <a href="#/">Technology</a>
                </li>
                <li>
                  <a href="#/">Pricing</a>
                </li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Legal</h4>
              <ul className="footer-links">
                <li>
                  <a href="#/">Privacy Policy</a>
                </li>
                <li>
                  <a href="#/">Terms & Conditions</a>
                </li>
                <li>
                  <a href="#/">Cookie Policy</a>
                </li>
                <li>
                  <a href="#/">Security</a>
                </li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Download the App</h4>
              <p className="footer-app-text">Get the Whooppe app for seamless event experiences.</p>
              <a href="https://play.google.com/store/apps/details?id=com.thrillathon.whooppeapp" target="_blank" rel="noopener noreferrer" className="btn-footer-download">
                <i className="fab fa-google-play"></i>
                <div className="btn-text">
                  <span className="btn-small">GET IT ON</span>
                  <span className="btn-large">Google Play</span>
                </div>
              </a>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 Whooppe · Thrillathon Technologies. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </main>
  );
}

export default Contact;
